import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import {
  Box, Chip, LinearProgress, Link as MuiLink, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { fetchRequest as fetchTrainings } from '../../redux/actions/trainings/list';
import { fetchRequest as downloadTraining } from '../../redux/actions/trainings/download';
import TrainingDetailsModal from './TrainingDetailsModal';
import DataTable from '../common/DataTable';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';

/**
 * Shared trainings list used by:
 *   - ModelTrainingsTab (single-pipeline view, modelId set)
 *   - TrainingsPage    (workspace-wide view, no modelId, with Pipeline column)
 *
 * Renders the unified DataTable plus the click-through
 * TrainingDetailsModal. Caller owns surrounding page chrome (filters,
 * page header).
 */

const defaultFormatDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); }
  catch (_e) { return String(v); }
};

const STATUS_TONE = {
  completed: 'success',
  success:   'success',
  failed:    'error',
  error:     'error',
  cancelled: 'error',
  queued:    'warning',
  skipped:   'warning',
  started:   'info',
  running:   'info',
  in_progress: 'info',
  unknown:   'neutral',
};

const STATUS_LABEL = {
  completed: 'Completed',
  success:   'Completed',
  failed:    'Failed',
  error:     'Failed',
  cancelled: 'Cancelled',
  queued:    'Queued',
  skipped:   'Skipped',
  started:   'Running',
  running:   'Running',
  in_progress: 'Running',
  unknown:   'Unknown',
};

const STAGE_LABEL = {
  preparing: 'Preparing',
  loading_data: 'Loading data',
  fitting: 'Fitting',
  scoring: 'Scoring',
  saving: 'Saving',
};

// Thin live progress bar shown under a running training's status chip.
// Driven by the row's ``progress`` subdoc ({stage, pct, epoch, total_epochs}),
// which the backend updates at each stage (+ per-epoch for TF).
const TrainingProgress = ({ progress }) => {
  if (!progress || typeof progress.pct !== 'number') return null;
  const pct = Math.max(0, Math.min(100, progress.pct));
  const stage = STAGE_LABEL[progress.stage] || 'Working';
  const epochs = progress.epoch && progress.total_epochs
    ? ` ${progress.epoch}/${progress.total_epochs}`
    : '';
  return (
    <Box sx={{ mt: 0.5, width: 110 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 4, borderRadius: 2 }}
      />
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
        {stage}{epochs} · {Math.round(pct)}%
      </Typography>
    </Box>
  );
};

// Lifecycle precedence used to break timestamp ties: synthetic rows
// (e.g. zero-shot champions) write queued/started/completed with the
// SAME timestamp, and a stable sort would surface "queued" for a row
// that is actually done. Later lifecycle stages rank higher.
const STATUS_RANK = {
  queued: 1, skipped: 1,
  started: 2, running: 2, in_progress: 2,
  completed: 3, success: 3, failed: 3, error: 3, cancelled: 3,
};

const getStatusFromArray = (statusArray) => {
  if (!statusArray || !Array.isArray(statusArray) || statusArray.length === 0) return 'unknown';
  const sorted = [...statusArray].sort((a, b) => {
    const dt = new Date(b.timestamp) - new Date(a.timestamp);
    if (dt !== 0) return dt;
    return (STATUS_RANK[b.action] || 0) - (STATUS_RANK[a.action] || 0);
  });
  return sorted[0].action;
};

const calculateDuration = (statusArray, durationInSeconds) => {
  if (durationInSeconds !== undefined && durationInSeconds !== null) return durationInSeconds;
  if (!statusArray || !Array.isArray(statusArray) || statusArray.length < 2) return null;
  const start = statusArray.find((s) => s.action === 'started');
  const end = statusArray.find(
    (s) => ['success', 'completed', 'failed', 'error'].includes(s.action)
  );
  if (!start || !end) return null;
  return Math.round((new Date(end.timestamp) - new Date(start.timestamp)) / 1000);
};

const formatDuration = (s) => {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
};

const formatDeploymentType = (t) => {
  switch (t) {
    case 'shared':
    case 'modelmesh_shared':   return 'Shared';
    case 'dedicated':
    case 'kserve_dedicated':   return 'Dedicated';
    case 'foundation':         return 'Foundation · shared';
    case 'external_fm':        return 'External FM · shared';
    default:                   return t || '—';
  }
};

const formatPromotionStatus = (s) => {
  if (!s) return '';
  return String(s).charAt(0).toUpperCase() + String(s).slice(1);
};

// A single "Deployed" chip when a training has any live deployment.
// Empty array => render nothing (a training is "not deployed").
const DeploymentBadges = ({ deployments, compact = false }) => {
  if (!deployments || deployments.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
        —
      </Typography>
    );
  }

  const tooltip = (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
        Deployed
      </Typography>
      {deployments.map((d, idx) => (
        <Typography key={d.promotion_id || idx} variant="caption" sx={{ display: 'block', fontSize: 11 }}>
          {formatDeploymentType(d.deployment_type)} · {formatPromotionStatus(d.status)}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Chip
        size="small"
        icon={<RocketLaunchIcon sx={{ fontSize: 12 }} />}
        label="Deployed"
        color="success"
        variant="filled"
        sx={{
          height: compact ? 18 : 20,
          fontSize: compact ? 10 : 11,
          fontWeight: 600,
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Tooltip>
  );
};

// Sparkline + headline (MAPE/MAE) for a single training row.
const PerformanceSparkline = ({ training }) => {
  const theme = useTheme();
  if (!training.individual_comparisons || training.individual_comparisons.length === 0) {
    return (
      <Box sx={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>—</Typography>
      </Box>
    );
  }

  const c = training.individual_comparisons[0];
  if (!c.ground_truth || !c.predicted || c.ground_truth.length === 0 || c.predicted.length === 0) {
    return (
      <Box sx={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>—</Typography>
      </Box>
    );
  }

  const maxPoints = 30;
  const len = Math.min(c.ground_truth.length, c.predicted.length, maxPoints);
  const groundTruth = c.ground_truth.slice(0, len);
  const predicted = c.predicted.slice(0, len);

  const absResiduals = predicted.map((p, i) => Math.abs(p - groundTruth[i]));
  const mae = absResiduals.reduce((a, b) => a + b, 0) / absResiduals.length;
  const absY = groundTruth.reduce((a, v) => a + Math.abs(v), 0);
  const wape = absY > 0 ? (absResiduals.reduce((a, b) => a + b, 0) / absY) * 100 : null;
  const pctErrs = predicted
    .map((p, i) => (groundTruth[i] !== 0 ? Math.abs(p - groundTruth[i]) / Math.abs(groundTruth[i]) : null))
    .filter((v) => v !== null);
  const mape = pctErrs.length ? (pctErrs.reduce((a, b) => a + b, 0) / pctErrs.length) * 100 : null;
  const fmt = (v) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    const abs = Math.abs(v);
    if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
    return v.toFixed(abs >= 1 ? 1 : 3);
  };
  const headline = wape !== null
    ? { value: `${wape.toFixed(1)}%`, label: 'WAPE' }
    : mape !== null
      ? { value: `${mape.toFixed(1)}%`, label: 'MAPE' }
      : { value: fmt(mae), label: 'MAE' };
  const errPct = wape != null ? wape : mape;
  const headlineColor =
    errPct == null ? theme.palette.text.secondary
    : errPct < 5 ? theme.palette.success.main
    : errPct < 15 ? theme.palette.warning.main
    : theme.palette.error.main;

  const chartData = [
    { id: 'Actual',    data: groundTruth.map((v, i) => ({ x: i, y: v })) },
    { id: 'Predicted', data: predicted.map((v, i) => ({ x: i, y: v })) },
  ];

  const ErrorBandLayer = ({ series, xScale, yScale }) => {
    if (!series || series.length < 2) return null;
    const a = series[0].data; const p = series[1].data;
    if (a.length !== p.length) return null;
    const segs = [];
    for (let i = 0; i < a.length - 1; i++) {
      const x0 = xScale(a[i].data.x); const x1 = xScale(a[i + 1].data.x);
      const aY0 = yScale(a[i].data.y); const aY1 = yScale(a[i + 1].data.y);
      const pY0 = yScale(p[i].data.y); const pY1 = yScale(p[i + 1].data.y);
      const meanD = ((p[i].data.y - a[i].data.y) + (p[i + 1].data.y - a[i + 1].data.y)) / 2;
      const fill = alpha(meanD >= 0 ? theme.palette.warning.main : theme.palette.error.main, 0.18);
      segs.push(<path key={i} d={`M ${x0},${aY0} L ${x1},${aY1} L ${x1},${pY1} L ${x0},${pY0} Z`} fill={fill} stroke="none" />);
    }
    return <g>{segs}</g>;
  };

  const DualLineLayer = ({ series, lineGenerator, xScale, yScale }) => {
    if (!series || series.length < 2) return null;
    const buildPath = (pts) => lineGenerator(pts.map((p) => ({ x: xScale(p.data.x), y: yScale(p.data.y) })));
    return (
      <g>
        <path d={buildPath(series[0].data)} fill="none"
          stroke={alpha(theme.palette.text.primary, 0.85)}
          strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        <path d={buildPath(series[1].data)} fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth={1.5} strokeDasharray="4 3"
          strokeLinejoin="round" strokeLinecap="round" />
      </g>
    );
  };

  return (
    <Box sx={{ height: 44, position: 'relative', minWidth: 130 }}>
      <Box sx={{
        position: 'absolute', top: 0, right: 0, zIndex: 2,
        display: 'flex', alignItems: 'baseline', gap: 0.4,
        bgcolor: alpha(theme.palette.background.paper, 0.85),
        px: 0.5, py: 0.1, borderRadius: 0.75,
        backdropFilter: 'blur(2px)', pointerEvents: 'none',
      }}>
        <Typography variant="caption" sx={{
          fontSize: 9, color: theme.palette.text.secondary,
          fontWeight: 600, letterSpacing: 0.4,
        }}>
          {headline.label}
        </Typography>
        <Typography variant="body2" sx={{
          fontWeight: 700, color: headlineColor, fontSize: 11,
          lineHeight: 1, fontVariantNumeric: 'tabular-nums',
        }}>
          {headline.value}
        </Typography>
      </Box>
      <ResponsiveLine
        data={chartData}
        margin={{ top: 4, right: 4, bottom: 2, left: 4 }}
        xScale={{ type: 'linear' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
        curve="monotoneX"
        axisTop={null} axisRight={null} axisBottom={null} axisLeft={null}
        enableGridX={false} enableGridY={false}
        enablePoints={false} enableArea={false}
        layers={['grid', 'axes', ErrorBandLayer, DualLineLayer, 'crosshair', 'mesh']}
        colors={[alpha(theme.palette.text.primary, 0.85), theme.palette.primary.main]}
        lineWidth={1.5}
        useMesh
        theme={{
          crosshair: { line: { stroke: theme.palette.primary.main, strokeWidth: 1, strokeOpacity: 0.35 } },
          tooltip: {
            container: {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontSize: 12, borderRadius: 4,
              boxShadow: theme.palette.boxShadow,
              padding: '6px 10px',
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
        tooltip={({ point }) => {
          const i = point.data.x;
          const a = groundTruth[i]; const p = predicted[i];
          if (a === undefined || p === undefined) return null;
          const err = p - a;
          const pctErr = a !== 0 ? (err / Math.abs(a)) * 100 : null;
          return (
            <div style={{
              background: theme.palette.background.paper,
              padding: '6px 10px',
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              boxShadow: theme.palette.boxShadow,
              minWidth: 140,
            }}>
              <div style={{ fontSize: 10, color: theme.palette.text.secondary, marginBottom: 3 }}>
                Step {i + 1}
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>Actual</span><strong>{fmt(a)}</strong>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>Predicted</span><strong>{fmt(p)}</strong>
              </div>
              <div style={{
                fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8,
                color: err >= 0 ? theme.palette.warning.main : theme.palette.error.main,
                fontWeight: 600,
              }}>
                <span>Error</span>
                <span>{err >= 0 ? '+' : ''}{fmt(err)}{pctErr !== null ? ` (${pctErr >= 0 ? '+' : ''}${pctErr.toFixed(1)}%)` : ''}</span>
              </div>
            </div>
          );
        }}
      />
    </Box>
  );
};

const TrainingsTable = ({
  modelId,
  filters,
  formatDate = defaultFormatDate,
  showPipelineColumn = false,
  emptyMessage = 'No training records available.',
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const trainings = useSelector((s) => s.trainings_list.response?.trainings) || [];
  const trainingsPagination = useSelector((s) => s.trainings_list.response?.pagination) || {};
  const isLoading = useSelector((s) => s.trainings_list.isLoading);
  const error = useSelector((s) => s.trainings_list.error);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);

  const modelIdsArr = filters?.modelIds || [];
  const segmentIdsArr = filters?.segmentIds || [];
  const statusesArr = filters?.statuses || [];
  const modelIdsKey = modelIdsArr.join(',');
  const segmentIdsKey = segmentIdsArr.join(',');
  const statusesKey = statusesArr.join(',');

  useEffect(() => { setPage(0); }, [modelIdsKey, segmentIdsKey, statusesKey, modelId]);

  const loadTrainings = useCallback(() => {
    const params = { page, rowsPerPage, sortColumn, sortDirection };
    if (modelId) params.modelId = modelId;
    if (modelIdsArr.length > 0) params.modelIds = modelIdsArr;
    if (segmentIdsArr.length > 0) params.segmentIds = segmentIdsArr;
    if (statusesArr.length > 0) params.statuses = statusesArr;
    dispatch(fetchTrainings(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, modelId, page, rowsPerPage, sortColumn, sortDirection,
      modelIdsKey, segmentIdsKey, statusesKey]);

  useEffect(() => { loadTrainings(); }, [loadTrainings]);

  // Live status (no polling): a training/deployment transition on the
  // workspace channel refetches the current page so rows flip
  // Queued -> Running -> Completed (+ deployment badges) as they happen.
  useRealtimeRefresh(eventPrefix('training.', 'deployment.'), loadTrainings);

  const handleOpenTrainingDetails = (id) => {
    setSelectedTrainingId(id);
    setIsModalOpen(true);
  };

  const scoreColor = (v) => {
    if (typeof v !== 'number') return theme.palette.text.secondary;
    if (v >= 80) return theme.palette.success.main;
    if (v >= 60) return theme.palette.primary.main;
    if (v >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (t) => {
        const safeStatus = t.status && Array.isArray(t.status)
          ? getStatusFromArray(t.status)
          : t.executed ? 'completed' : 'failed';
        const running = ['started', 'running', 'in_progress'].includes(safeStatus);
        return (
          <Box>
            <EntityCell
              id={t.uid}
              tone={STATUS_TONE[safeStatus] || 'neutral'}
              label={STATUS_LABEL[safeStatus] || safeStatus}
            />
            {running && <TrainingProgress progress={t.progress} />}
          </Box>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 160,
      render: (t) => (
        <Tooltip title={formatDate(t.created_at)} arrow placement="top">
          <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.3 }}>
            {formatDate(t.created_at)}
          </Typography>
        </Tooltip>
      ),
    },
    ...(showPipelineColumn ? [{
      key: 'pipeline',
      label: 'Pipeline',
      width: '20%',
      render: (t) => (
        t.model_id ? (
          <Box>
            <MuiLink
              component="button" type="button" underline="hover"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/pipeline/${t.model_id}`);
              }}
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500, fontSize: 13,
                textAlign: 'left',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: '100%', display: 'block',
                '&:hover': { color: theme.palette.primary.main },
              }}
            >
              {t.model_name || t.model_id}
            </MuiLink>
            {t.blueprint_name && (
              <Typography variant="caption" color="text.secondary" sx={{
                display: 'block', fontSize: 11,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {t.blueprint_name}
              </Typography>
            )}
          </Box>
        ) : <Typography variant="body2" color="text.secondary">—</Typography>
      ),
    }] : []),
    {
      key: 'accuracy_score',
      label: 'Score',
      sortable: true,
      width: 80,
      align: 'right',
      render: (t) => {
        if (typeof t.accuracy_score !== 'number') return <Typography variant="body2" color="text.secondary">—</Typography>;
        const isClf = t.score_kind === 'classification';
        return (
          <AccuracyInfoTooltip value={t.accuracy_score} scoreKind={t.score_kind}>
            <Box sx={{ textAlign: 'right', cursor: 'help' }}>
              <Typography variant="body2" sx={{
                color: scoreColor(t.accuracy_score),
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 13,
              }}>
                {`${t.accuracy_score.toFixed(1)}%`}
              </Typography>
              {isClf && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  AUC
                </Typography>
              )}
            </Box>
          </AccuracyInfoTooltip>
        );
      },
    },
    {
      key: 'duration',
      label: 'Duration',
      sortable: true,
      width: 90,
      render: (t) => (
        <Typography variant="body2" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
          {formatDuration(calculateDuration(t.status, t.duration))}
        </Typography>
      ),
    },
    {
      key: 'cost',
      label: 'Cost',
      sortable: true,
      width: 100,
      align: 'right',
      render: (t) => {
        const v = t.cost !== '' && t.cost !== null && t.cost !== undefined
          ? parseFloat(t.cost) : 0;
        return (
          <Tooltip title={`${v.toFixed(4)} ${t.cost_unit || 'credits'}`} arrow placement="top">
            <Typography variant="body2" sx={{
              fontSize: 13, fontVariantNumeric: 'tabular-nums',
              color: theme.palette.text.secondary,
            }}>
              {v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      key: 'deployments',
      label: 'Deployment',
      width: 140,
      render: (t) => <DeploymentBadges deployments={t.deployments} />,
    },
    {
      key: 'performance',
      label: 'Trend',
      width: 150,
      render: (t) => <PerformanceSparkline training={t} />,
    },
    {
      key: 'message',
      label: '',
      width: 140,
      render: (t) => {
        const safeStatus = t.status && Array.isArray(t.status)
          ? getStatusFromArray(t.status)
          : t.executed ? 'completed' : 'failed';
        if ((safeStatus === 'failed' || safeStatus === 'error') && t.message) {
          return (
            <Tooltip title={t.message} arrow placement="top">
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.4,
                color: theme.palette.error.main,
                maxWidth: 140,
              }}>
                <ErrorOutlineIcon sx={{ fontSize: 12 }} />
                <Typography variant="caption" sx={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontSize: 11,
                }}>
                  {t.message}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
        return null;
      },
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      align: 'right',
      stopRowClick: true,
      render: (t) => {
        // Trained artifacts are exportable for custom (and BYOM)
        // trainings that completed serialization. Foundation LoRA
        // trainings reference shared backbone weights - not exportable.
        const downloadable = t.training_kind === 'custom' && t.executed;
        return (
          <RowActions
            onOpen={() => handleOpenTrainingDetails(t.uid)}
            openLabel="View training details"
            items={downloadable ? [{
              label: 'Download model',
              icon: FileDownloadOutlinedIcon,
              onClick: () => dispatch(downloadTraining({ training_id: t.uid })),
            }] : undefined}
          />
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={trainings}
        getRowKey={(row, idx) => row.uid || idx}
        onRowClick={(row) => handleOpenTrainingDetails(row.uid)}
        loading={isLoading}
        error={error}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={trainingsPagination?.total_items || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyState={{ title: 'No trainings found', message: emptyMessage }}
      />

      <TrainingDetailsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        trainingId={selectedTrainingId}
        formatDate={formatDate}
      />
    </>
  );
};

export default TrainingsTable;
